import { memo, useMemo } from "react";
import {
  MARKER_OPACITY_AT_HIGH_ZOOM,
  MARKER_OPACITY_AT_LOW_ZOOM,
} from "../BatchedCircles/constants";
import { buildBatchedRectPaths } from "./buildBatchedRectPaths";
import { computeBboxGeometry } from "./computeBboxGeometry";
import { computeBboxStrokeWidth } from "./computeBboxStrokeWidth";
import { SelectedBboxRect } from "./SelectedBboxRect";
import { BboxEditHandles } from "./BboxEditHandles";
import { BboxRipplePaths } from "./BboxRipplePaths";
import { BBOX_STROKE_PX, SELECTED_BBOX_STROKE_PX } from "./constants";
import type { BatchedRectsProps } from "./types";

export const BatchedRects = memo(function BatchedRects({
  objects,
  selectedObject,
  zoomLevel,
  opacityThreshold,
  maxZoom,
  onSelectedBboxChange,
}: BatchedRectsProps) {
  const strokeWidth = computeBboxStrokeWidth({
    zoomLevel,
    maxZoom,
    strokePxAtMaxZoom: BBOX_STROKE_PX,
  });

  const selectedStrokeWidth = computeBboxStrokeWidth({
    zoomLevel,
    maxZoom,
    strokePxAtMaxZoom: SELECTED_BBOX_STROKE_PX,
  });

  const { isLowZoom, inflatePdf } = useMemo(
    () =>
      computeBboxGeometry({
        zoomLevel,
        opacityThreshold,
        strokeWidth,
      }),
    [zoomLevel, opacityThreshold, strokeWidth],
  );

  const batchedPaths = useMemo(
    () =>
      buildBatchedRectPaths({
        objects,
        isLowZoom,
        excludeId: selectedObject?.id,
        inflatePdf,
      }),
    [objects, isLowZoom, selectedObject?.id, inflatePdf],
  );

  // Ripple paths are always rendered (CSS class on a parent triggers the animation).
  const ripplePaths = useMemo(
    () => buildBatchedRectPaths({ objects, isLowZoom: false, excludeId: null }),
    [objects],
  );

  const selectedObjectId = selectedObject?.id;
  const isSelectedInObjects = useMemo(
    () =>
      selectedObjectId !== undefined &&
      objects.some((object) => object.id === selectedObjectId),
    [objects, selectedObjectId],
  );

  const showSelected = !!selectedObject?.bbox && isSelectedInObjects;

  const selectedFillOpacity =
    isLowZoom && selectedObject?.status !== "uninstalled"
      ? MARKER_OPACITY_AT_LOW_ZOOM
      : MARKER_OPACITY_AT_HIGH_ZOOM;

  return (
    <>
      {batchedPaths.map((batch) => (
        <path
          key={batch.color}
          d={batch.pathData}
          fill={batch.color}
          fillOpacity={batch.opacity}
        />
      ))}
      {!isLowZoom &&
        batchedPaths.map((batch) => (
          <path
            key={`${batch.color}-stroke`}
            d={batch.pathData}
            fill="none"
            stroke={batch.color}
            strokeWidth={strokeWidth}
          />
        ))}
      {showSelected && selectedObject && (
        <SelectedBboxRect
          selectedObject={selectedObject}
          inflatePdf={inflatePdf}
          strokeWidth={selectedStrokeWidth}
          fillOpacity={selectedFillOpacity}
        />
      )}
      {showSelected && selectedObject && onSelectedBboxChange && (
        <BboxEditHandles
          bbox={selectedObject.bbox}
          inflatePdf={inflatePdf}
          zoomLevel={zoomLevel}
          onChange={onSelectedBboxChange}
        />
      )}
      <BboxRipplePaths ripplePaths={ripplePaths} zoomLevel={zoomLevel} />
    </>
  );
});
